// Habilitar 2FA, gate de `403 mfa_required` sem 2FA (`spec/17-frontend.md` §8.2).
// O QR é gerado no cliente a partir do `totpURI` (por isso `img-src data:` na CSP,
// `spec/17` §9.3). Os backup codes aparecem UMA vez.

import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import QRCode from 'qrcode';
import { useState } from 'react';
import { enableTwoFactor, verifyTotp } from '../api/auth.ts';
import { errorMessageOf } from '../api/errors.ts';
import { AuthShell, FormError } from '../components/app/auth-shell.tsx';
import { Mono } from '../components/kit/mono.tsx';
import { Button } from '../components/ui/button.tsx';
import { Input } from '../components/ui/input.tsx';
import { Label } from '../components/ui/label.tsx';

interface Enrollment {
  qr: string;
  backupCodes: string[];
}

function Setup2fa() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [enrollment, setEnrollment] = useState<Enrollment | undefined>(undefined);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const start = async (): Promise<void> => {
    setBusy(true);
    setError(undefined);
    try {
      const result = await enableTwoFactor(password);
      setEnrollment({
        qr: await QRCode.toDataURL(result.totpURI, { margin: 1, width: 220 }),
        backupCodes: result.backupCodes,
      });
    } catch (caught) {
      setError(errorMessageOf(caught));
    } finally {
      setBusy(false);
    }
  };

  const finish = async (): Promise<void> => {
    setBusy(true);
    setError(undefined);
    try {
      await verifyTotp(code.trim());
      await queryClient.invalidateQueries();
      await navigate({ to: '/admin' });
    } catch (caught) {
      setError(errorMessageOf(caught));
      setBusy(false);
    }
  };

  if (enrollment === undefined) {
    return (
      <AuthShell
        title="Habilite a 2FA"
        description="O backoffice só abre com segundo fator. Confirme sua senha para gerar o QR."
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void start();
          }}
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="confirm-password">Senha</Label>
            <Input
              id="confirm-password"
              type="password"
              autoComplete="current-password"
              className="h-11 bg-input"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          </div>
          <FormError message={error} />
          <Button type="submit" className="h-11" disabled={busy || password === ''}>
            {busy ? 'Gerando…' : 'Gerar QR code'}
          </Button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Escaneie o QR"
      description="Abra seu app de autenticação, escaneie e digite o código para concluir."
    >
      <div className="flex flex-col gap-4">
        <img
          src={enrollment.qr}
          alt="QR code do segundo fator"
          className="self-center rounded-card border border-border bg-white p-2"
          width={220}
          height={220}
        />

        <div className="flex flex-col gap-2 rounded-card border border-card-border bg-card p-4">
          <div className="micro-label">Backup codes</div>
          <p className="text-11 text-muted">Guarde agora: eles não aparecem de novo.</p>
          <ul className="grid grid-cols-2 gap-1">
            {enrollment.backupCodes.map((backupCode) => (
              <li key={backupCode}>
                <Mono className="text-14 text-text">{backupCode}</Mono>
              </li>
            ))}
          </ul>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => {
              navigator.clipboard
                .writeText(enrollment.backupCodes.join('\n'))
                .then(() => {
                  setCopied(true);
                })
                .catch(() => {
                  setCopied(false);
                });
            }}
          >
            {copied ? 'Copiado' : 'Copiar códigos'}
          </Button>
        </div>

        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void finish();
          }}
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="setup-code">Código do app</Label>
            <Input
              id="setup-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              className="num h-11 bg-input text-16 tracking-[0.3em]"
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
              }}
            />
          </div>
          <FormError message={error} />
          <Button type="submit" className="h-11" disabled={busy || code.trim() === ''}>
            {busy ? 'Confirmando…' : 'Concluir'}
          </Button>
        </form>
      </div>
    </AuthShell>
  );
}

export const Route = createFileRoute('/admin/setup-2fa')({ component: Setup2fa });
